/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { Divider, Heading, Image, Link, List, Text } from 'pcln-design-system';
import React from 'react';
import styled from 'styled-components';
import ContentfulImage from '../ContentfulImage';
import ContentfulList from '../ContentfulList';
import { processText } from '../utils';
import Table from './Table';
const WrappingLink = /*#__PURE__*/styled(Link).withConfig({
  displayName: "ContentfulRichText__WrappingLink",
  componentId: "sc-1fwdgkk-0"
})(["word-break:break-word;"]);
const InlineImage = /*#__PURE__*/styled(Image).withConfig({
  displayName: "ContentfulRichText__InlineImage",
  componentId: "sc-1fwdgkk-1"
})(["display:inline-block;"]);
const InlineContentfulImage = /*#__PURE__*/styled(ContentfulImage).withConfig({
  displayName: "ContentfulRichText__InlineContentfulImage",
  componentId: "sc-1fwdgkk-2"
})(["display:inline-block;"]);
const getEmbeddedAsset = (node, props) => {
  const assetProps = node.data.target;
  if (assetProps?.file?.contentType?.includes('image')) {
    const url = assetProps.file.url;
    return React.createElement(InlineImage, {
      src: `https:${url}`,
      mx: 1,
      ...props
    });
  }
};
const getInlineEmbeddedEntry = (node, props) => {
  const entryProps = node.data.target;
  if (entryProps?.contentTypeId === 'image') {
    return React.createElement(InlineContentfulImage, {
      ...entryProps,
      mx: 1,
      ...props
    });
  }
  if (entryProps?.contentTypeId === 'list') {
    return React.createElement(ContentfulList, {
      ...entryProps,
      ...props
    });
  }
  if (entryProps?.contentTypeId === 'link') {
    function handleClick(e) {
      e.stopPropagation();
      if (props.onClick) {
        props.onClick(entryProps);
      }
    }
    return React.createElement(Link, {
      href: entryProps.longUrl || entryProps.url,
      "aria-label": entryProps.ariaLabel,
      target: entryProps.openInNewTab ? '_blank' : undefined,
      onClick: handleClick
    }, entryProps.label);
  }
};
const Paragraph = ({
  children,
  ...props
}) => React.createElement(Text.p, {
  ...props
}, children);
const BoldText = ({
  children,
  ...props
}) => React.createElement(Text.span, {
  fontWeight: 'bold',
  ...props
}, children);
const ItalicText = ({
  children,
  ...props
}) => React.createElement(Text.span, {
  fontStyle: 'italic',
  ...props
}, children);
const LinkText = ({
  children,
  node,
  ...props
}) => {
  const href = node.data.uri;
  const isRelativeLink = href?.indexOf('#') === 0 || href?.indexOf('/') === 0;
  return React.createElement(WrappingLink, {
    target: isRelativeLink ? '_self' : '_blank',
    href: href,
    ...props
  }, children);
};
function getChildrenText(children) {
  const ATTEMPT_LIMIT = 10;
  let currChildren = children[0];
  let attempts = 1;
  let elementId = null;
  while (typeof currChildren === 'object' && Object.keys(currChildren).length !== 2 && attempts < ATTEMPT_LIMIT) {
    if (typeof currChildren.props === 'object') {
      currChildren = currChildren.props.children;
    }
    attempts++;
  }
  if (attempts < ATTEMPT_LIMIT && typeof currChildren === 'object') {
    elementId = currChildren[1];
  }
  return elementId;
}
function getMark(type, text, props) {
  switch (type) {
    case MARKS.BOLD:
      return React.createElement(BoldText, {
        ...props
      }, text);
    case MARKS.ITALIC:
      return React.createElement(ItalicText, {
        ...props
      }, text);
    case MARKS.SUBSCRIPT:
      return React.createElement("sub", null, React.createElement(Text.span, {
        ...props
      }, text));
    case MARKS.SUPERSCRIPT:
      return React.createElement("sup", null, React.createElement(Text.span, {
        ...props
      }, text));
  }
}
function isEmptyTrailingNode(node) {
  const nodeContentValue = node?.content?.[0]?.value;
  return typeof nodeContentValue === 'string' && nodeContentValue === '' && node.content.length === 1;
}
function getNode(type, node, children, props) {
  const {
    fontSize,
    textStyle,
    ...propsNoFontSizeAndTextStyle
  } = props;
  const {
    color,
    ...propsNoColor
  } = props;
  const {
    listStyle,
    orderedListStyle,
    unorderedListStyle,
    ...propsNoListStyle
  } = props;
  const textId = children ? getChildrenText(children) : null;
  const headingProps = {
    ...propsNoFontSizeAndTextStyle,
    ...(textId ? {
      id: textId
    } : {})
  };
  if (type === 'hyperlink' && props.partnerUrl) {
    node.data.uri = node.data.uri.replaceAll(/{{{partnerUrl}}}/g, props.partnerUrl);
  }
  if (isEmptyTrailingNode(node)) {
    return;
  }
  switch (type) {
    case BLOCKS.HEADING_1:
      return React.createElement(Heading, {
        as: 'h1',
        textStyle: 'heading1',
        ...headingProps
      }, children);
    case BLOCKS.HEADING_2:
      return React.createElement(Heading, {
        as: 'h2',
        textStyle: 'heading2',
        ...headingProps
      }, children);
    case BLOCKS.HEADING_3:
      return React.createElement(Heading, {
        as: 'h3',
        textStyle: 'heading3',
        ...headingProps
      }, children);
    case BLOCKS.HEADING_4:
      return React.createElement(Heading, {
        as: 'h4',
        textStyle: 'heading4',
        ...headingProps
      }, children);
    case BLOCKS.HEADING_5:
      return React.createElement(Heading, {
        as: 'h5',
        textStyle: 'heading5',
        ...headingProps
      }, children);
    case BLOCKS.HEADING_6:
      return React.createElement(Heading, {
        as: 'h6',
        textStyle: 'heading6',
        ...headingProps
      }, children);
    case BLOCKS.PARAGRAPH:
      return React.createElement(Paragraph, {
        ...props
      }, children);
    case BLOCKS.HR:
      return React.createElement(Divider, {
        ...props
      });
    case BLOCKS.UL_LIST:
      return React.createElement(List.ul, {
        ...propsNoListStyle,
        listStyle: unorderedListStyle ?? listStyle
      }, children);
    case BLOCKS.OL_LIST:
      return React.createElement(List.ol, {
        ...propsNoListStyle,
        listStyle: orderedListStyle ?? listStyle
      }, children);
    case BLOCKS.TABLE:
      const {
        tableProps,
        tableOverride
      } = props;
      const {
        hasOuterBorder = true,
        innerBorderType = 'all',
        isFullWidth = true,
        ...rest
      } = tableProps || {};
      const TableNode = tableOverride || Table;
      const [header, ...body] = children;
      return React.createElement(TableNode, {
        hasOuterBorder: hasOuterBorder,
        innerBorderType: innerBorderType,
        isFullWidth: isFullWidth,
        ...rest
      }, React.createElement("thead", null, header), React.createElement("tbody", null, body));
    case BLOCKS.TABLE_ROW:
      return React.createElement("tr", {
        ...props
      }, children);
    case BLOCKS.TABLE_HEADER_CELL:
      return React.createElement("th", {
        ...props
      }, children);
    case BLOCKS.TABLE_CELL:
      return React.createElement("td", {
        ...props
      }, children);
    case BLOCKS.EMBEDDED_ASSET:
      return getEmbeddedAsset(node, props);
    case INLINES.EMBEDDED_ENTRY:
      return getInlineEmbeddedEntry(node, props);
    case INLINES.HYPERLINK:
      return React.createElement(LinkText, {
        ...propsNoColor,
        node: node
      }, children);
  }
}
const options = props => {
  return {
    renderText: text => processText(text, props),
    renderMark: {
      [MARKS.BOLD]: text => getMark(MARKS.BOLD, text, props),
      [MARKS.ITALIC]: text => getMark(MARKS.ITALIC, text, props),
      [MARKS.SUBSCRIPT]: text => getMark(MARKS.SUBSCRIPT, text, props),
      [MARKS.SUPERSCRIPT]: text => getMark(MARKS.SUPERSCRIPT, text, props)
    },
    renderNode: {
      [BLOCKS.HEADING_1]: (node, children) => getNode(BLOCKS.HEADING_1, node, children, props),
      [BLOCKS.HEADING_2]: (node, children) => getNode(BLOCKS.HEADING_2, node, children, props),
      [BLOCKS.HEADING_3]: (node, children) => getNode(BLOCKS.HEADING_3, node, children, props),
      [BLOCKS.HEADING_4]: (node, children) => getNode(BLOCKS.HEADING_4, node, children, props),
      [BLOCKS.HEADING_5]: (node, children) => getNode(BLOCKS.HEADING_5, node, children, props),
      [BLOCKS.HEADING_6]: (node, children) => getNode(BLOCKS.HEADING_6, node, children, props),
      [BLOCKS.PARAGRAPH]: (node, children) => getNode(BLOCKS.PARAGRAPH, node, children, props),
      [BLOCKS.HR]: () => getNode(BLOCKS.HR, null, null, props),
      [BLOCKS.UL_LIST]: (node, children) => getNode(BLOCKS.UL_LIST, node, children, props),
      [BLOCKS.OL_LIST]: (node, children) => getNode(BLOCKS.OL_LIST, node, children, props),
      [BLOCKS.TABLE]: (node, children) => getNode(BLOCKS.TABLE, node, children, props),
      [BLOCKS.TABLE_ROW]: (node, children) => getNode(BLOCKS.TABLE_ROW, node, children, props),
      [BLOCKS.TABLE_HEADER_CELL]: (node, children) => getNode(BLOCKS.TABLE_HEADER_CELL, node, children, props),
      [BLOCKS.TABLE_CELL]: (node, children) => getNode(BLOCKS.TABLE_CELL, node, children, props),
      [BLOCKS.EMBEDDED_ASSET]: (node, children) => getNode(BLOCKS.EMBEDDED_ASSET, node, children, props),
      [INLINES.EMBEDDED_ENTRY]: (node, children) => getNode(INLINES.EMBEDDED_ENTRY, node, children, props),
      [INLINES.HYPERLINK]: (node, children) => getNode(INLINES.HYPERLINK, node, children, props)
    }
  };
};
/** @public */
function ContentfulRichText({
  body,
  ...props
}) {
  return React.createElement(React.Fragment, null, documentToReactComponents(body, options(props)));
}
export default ContentfulRichText;